import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ApplicationCooLayout from "../../layout/application-coo-layout";
import { TextSubheader } from "../../components/text/text-subheader";
import { useAppSelector } from "../../redux/hook";
import { SectionCard } from "../../components/card";
import { Separator } from "../../components/separator";
import { P2Button, P2ButtonGroup, P2Checkbox, P2Modal, P2TextDescription } from "react-bootstrap-common-script";
import FeeAndGst from "../application-coo-stage-c/stage-c-fee-cost";
import { useNavigate } from "react-router-dom";
import { PolymerAxios } from "../../utils/axios";
import { PaymentMethod } from "../../components/payment-method";
import { LottieLoader } from "../../components/loader";
import config from "../../config";
import { ProcessPayment } from "../../models/classes/stage-c/Process-Payment.class";
import { PaymentMethodEnum } from "../../models/enum/payment-method.enum";
import { API_ApplicationCancellationPayment, API_ApplicationDetail, API_ApplicationProcessCancellationPayment, API_DraftDetail } from "../../utils/api";
import { useGetCancellationFeesQuery } from "../../redux/services/getPaymentInfo";
import { useMapDraft } from "../../hooks/use-map-draft";

export default function CancelApplicationPayment() {
  // Query params
  const queryParameters = new URLSearchParams(window.location.search);
  const application_uuid = queryParameters.get("application_id");
  const isDebug = queryParameters.get("debug");

  // States
  const startApplicationFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const applicationDetails = useAppSelector((state) => state.app.ApplicationCancelledSlice).application_details;
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);

  const { performDraftMapping } = useMapDraft();

  const [isTncChecked, setIsTncChecked] = useState<boolean>(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [isPaymentMethod, setIsPaymentMethod] = useState<string>("");

  const { data, isError, isLoading } = useGetCancellationFeesQuery(
    { application_uuid: application_uuid ?? "", singpass_token: singpassToken ?? "" },
    { skip: !application_uuid }
  );

  // Refs
  const paymentFormRef = useRef<HTMLFormElement>(null);

  // Derived values
  const paymentUrl = document.location.origin === "https://services3.hdb.gov.sg" ? config.prodPaymentURL : config.devAndStgPaymentURL;
  const mortgageFeeForCurrent = useMemo(
    () => data?.find(({ fee_type, owner_type }) => fee_type === "MORTGAGE_CONVEYANCING" && owner_type === "CURRENT_OWNER"),
    [data]
  );
  const transferFeeForCurrent = useMemo(
    () => data?.find(({ fee_type, owner_type }) => fee_type === "TRANSFER_CONVEYANCING" && owner_type === "CURRENT_OWNER"),
    [data]
  );
  const mortgageFeeForProposed = useMemo(
    () => data?.find(({ fee_type, owner_type }) => fee_type === "MORTGAGE_CONVEYANCING" && owner_type === "PROPOSED_OWNER"),
    [data]
  );
  const transferFeeForProposed = useMemo(
    () => data?.find(({ fee_type, owner_type }) => fee_type === "TRANSFER_CONVEYANCING" && owner_type === "PROPOSED_OWNER"),
    [data]
  );
  const totalFee = useMemo(() => (data ? data.reduce((a, b) => a + (b.fee_cost ?? 0), 0) : 0), [data]);
  const isContinueButtonEnabled = isTncChecked && !isLoading && !isError && (isPaymentMethod !== "");

  // Functions
  let navigate = useNavigate();

  const handlePaymentHandler = useCallback((value: string) => {
    setIsPaymentMethod(value);
  },[]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const continuePayment = useCallback(async () => {
    setIsPaymentLoading(true);
    try {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationProcessCancellationPayment, { application_uuid });

      // Defaults to card payment because at the point of writing, the portal only supports card payment.
      // Please change when necessary.
      const processPaymentObj = new ProcessPayment({
        application_uuid: application_uuid ?? "",
        payment_mode: isPaymentMethod as PaymentMethodEnum,
        transaction_types: ["conveyancing"],
      });
      const paymentResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(
        API_ApplicationCancellationPayment,
        processPaymentObj.json()
      );

      document.getElementById("ttcNumPayReq")?.setAttribute("value", paymentResponse?.data?.ttcNum);
      document.getElementById("systIdTagPayReq")?.setAttribute("value", paymentResponse?.data?.systIdTagPayReq);
      document.getElementById("systIdforPaymentPayReq")?.setAttribute("value", "BR20");
      setIsPaymentLoading(false);

      paymentFormRef.current?.submit();
    } finally {
      setIsPaymentLoading(false);
    }
  }, [application_uuid, loginUser?.uinfin, singpassToken]);

  // Effects
  useEffect(() => {
    const checkData = async () => {
      const isDataMappedToSlice = applicationStep1FormData.reason.reason !== "";
  
      if (!loginUser) navigate(`/application-coo?application_id=${application_uuid}`);
  
      if (!isDataMappedToSlice || (applicationStep1FormData.reason.registrar_uinfin && applicationStep1FormData.reason.registrar_uinfin === loginUser.uinfin)) {
        const url =
          applicationDetails?.application_status === "SAVE_DRAFT"
            ? `${API_DraftDetail}?applicationUuid=${application_uuid}`
            : `${API_ApplicationDetail}?applicationUuid=${application_uuid}`;
        const { data } = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(url);
        performDraftMapping(data);
      }
    }

    checkData();
  }, [loginUser, applicationStep1FormData, applicationDetails, navigate]);

  return (
    <>
      <form ref={paymentFormRef} method="POST" action={paymentUrl}>
        <input type={isDebug === "true" ? "" : "hidden"} name="ttcNumPayReq" id="ttcNumPayReq" value={""} />
        <input type={isDebug === "true" ? "" : "hidden"} name="systIdforPaymentPayReq" id="systIdforPaymentPayReq" value={"BR20"} />
        <input type={isDebug === "true" ? "" : "hidden"} name="systIdTagPayReq" id="systIdTagPayReq" value={""} />
        {isDebug === "true" && <input type="submit" />}
      </form>
      <ApplicationCooLayout>
        <div style={{ padding: "62px 48px 0px" }}>
          <TextSubheader subheaderMain="Cancel Application Payment" subheaderSub={startApplicationFormData.selected_hdb_property?.address} />
          <div className="d-flex justify-content-center">
            <SectionCard
              required={false}
              className="flex-fill polymer-payment-section"
              titleChildren={<P2TextDescription title={"Payment"} />}
              contentChildren={
                <div className="polymer-main-column-container">
                  <p className="polymer-main-column-header" style={{ marginBottom: 16 }}>
                    Your application has been approved earlier, and conveyancing have begun.
                  </p>
                  <p className="polymer-main-column-header" style={{ marginBottom: 16 }}>
                    The following fees (inclusive of GST) are payable for confirmation of cancellation.
                  </p>
                  <div className="polymer-fees-container">
                    <div className="polymer-fees-header">
                      <p className="polymer-fees-subheader">Cancellation Fees</p>
                    </div>
                    <Separator />

                    {/* Current Owner Fees */}
                    {!!mortgageFeeForCurrent && mortgageFeeForCurrent.fee_cost !== 0 ? (
                      <div key={mortgageFeeForCurrent.fee_type} className="polymer-fees-text">
                        <div className="polymer-fees-gst-container">
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            Half Conveyancing Fee for Mortgage for Current Owners
                          </p>
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            {typeof mortgageFeeForCurrent.fee_cost === "number"
                              ? `$${mortgageFeeForCurrent.fee_cost?.toFixed(2)}`
                              : mortgageFeeForCurrent.fee_cost}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {!!transferFeeForCurrent && transferFeeForCurrent.fee_cost !== 0 ? (
                      <div key={transferFeeForCurrent.fee_type} className="polymer-fees-text">
                        <div className="polymer-fees-gst-container">
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            Half Conveyancing Fee for Transfer for Current Owners
                          </p>
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            {typeof transferFeeForCurrent.fee_cost === "number"
                              ? `$${transferFeeForCurrent.fee_cost?.toFixed(2)}`
                              : transferFeeForCurrent.fee_cost}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {(!!mortgageFeeForCurrent && mortgageFeeForCurrent.fee_cost !== 0) || (!!transferFeeForCurrent && transferFeeForCurrent.fee_cost !== 0) ? (
                      <Separator />
                    ) : null}

                    {/* Proposed Owner Fees */}
                    {!!mortgageFeeForProposed && mortgageFeeForProposed.fee_cost !== 0 ? (
                      <div key={mortgageFeeForProposed.fee_type} className="polymer-fees-text">
                        <div className="polymer-fees-gst-container">
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            Half Conveyancing Fee for Mortgage for Proposed Owners
                          </p>
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            {typeof mortgageFeeForProposed.fee_cost === "number"
                              ? `$${mortgageFeeForProposed.fee_cost?.toFixed(2)}`
                              : mortgageFeeForProposed.fee_cost}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {!!transferFeeForProposed && transferFeeForProposed.fee_cost !== 0 ? (
                      <div key={transferFeeForProposed.fee_type} className="polymer-fees-text">
                        <div className="polymer-fees-gst-container">
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            Half Conveyancing Fee for Transfer for Proposed Owners
                          </p>
                          <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                            {typeof transferFeeForProposed.fee_cost === "number"
                              ? `$${transferFeeForProposed.fee_cost?.toFixed(2)}`
                              : transferFeeForProposed.fee_cost}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {(!!mortgageFeeForProposed && mortgageFeeForProposed.fee_cost !== 0) ||
                    (!!transferFeeForProposed && transferFeeForProposed.fee_cost !== 0) ? (
                      <Separator />
                    ) : null}

                    {/* Total Fees */}
                    <div className="polymer-fees-total-container">
                      <p className="polymer-fees-700" style={{ fontSize: 18 }}>
                        Total:
                      </p>
                      <FeeAndGst fee_cost={totalFee} style={{ fontSize: 18 }} />
                    </div>
                  </div>
                  <PaymentMethod PaymentHandler={handlePaymentHandler} selectedPaymentMethod={isPaymentMethod} />
                  <div className="polymer-main-column-spacer">
                    <Separator />
                  </div>
                  <div className="polymer-main-column-tnc">
                    <p className="polymer-main-column-tnc-title">Terms & Conditions:</p>
                    <p className="polymer-main-column-tnc-content">
                      Please note that all fees paid are non-refundable in the event of any cancellation or rejection of the application. This includes the
                      cancellation of the application should submission of information or documents are not provided within the specified timeframe which deem
                      the application as incomplete, or incorrect. For updates on the application progress, you may check our progress tracker once the
                      application is submitted successfully.
                    </p>
                    <P2Checkbox
                      isActive={isTncChecked}
                      onClick={() => {
                        setIsTncChecked((value) => !value);
                      }}
                      description={<p className="m-0 polymer-main-column-tnc-checkbox">I have read and understood the above Terms & Conditions.</p>}
                    />
                  </div>
                </div>
              }
            />
          </div>
          <Separator style={{ margin: "32px 42px" }} />
          <div className="polymer-application-coo__card-button">
            <P2ButtonGroup>
              <P2Button variant={"outline-primary"} onClick={navigateBack}>
                Cancel
              </P2Button>
              <P2Button variant={"primary-1"} onClick={continuePayment} disabled={!isContinueButtonEnabled}>
                Continue
              </P2Button>
            </P2ButtonGroup>
          </div>
        </div>
        <P2Modal isOpen={isPaymentLoading} children={<LottieLoader />} />
      </ApplicationCooLayout>
    </>
  );
}
